import React from 'react'
import { useParams, Link } from 'react-router-dom'
import PageHeader from '@/components/Common/PageHeader'
import SEOHead from '@/components/SEO/SEOHead'
import { clampDescription, shortenTitle } from '@/utils/meta'
import { contactInfo } from '@/data/contact'
import { toTelHref } from '@/utils/phone'
import { buildGraph, breadcrumbListData, medicalWebPageSchema } from '@/data/structured-data'
import { allDiseases, dogDiseases, catDiseases } from '@/data/diseases'
import { motion } from 'framer-motion'
import { AlertTriangle, Stethoscope, Shield, Pill } from 'lucide-react'

const DiseaseDetail: React.FC = () => {
  const { slug } = useParams()
  const disease = allDiseases.find(d => d.slug === slug)

  if (!disease) {
    return (
      <section className="section-padding">
        <div className="container-custom">
          <div className="bg-white border rounded-xl p-8 text-center">
            <h2 className="text-xl font-bold mb-2">İçerik bulunamadı</h2>
            <p className="text-gray-600 mb-4">Aradığınız rehber içeriği mevcut değil.</p>
            <Link to="/hastaliklari" className="text-primary-600 font-semibold">Hastalık rehberine dön</Link>
          </div>
        </div>
      </section>
    )
  }

  const categorySlug = disease.category === 'dog' ? 'kopek' : 'kedi'
  const categoryLabel = disease.category === 'dog' ? 'Köpek Hastalıkları' : 'Kedi Hastalıkları'
  const diseaseUrl = `/hastaliklari/${categorySlug}/${disease.slug}`
  // Başlık ve açıklama Google'ın kesme sınırları içinde tutulur.
  const metaTitle = shortenTitle(disease.title)
  const telHref = toTelHref(contactInfo.phone)
  const speciesHub = disease.category === 'dog' ? '/kopek' : '/kedi'
  const speciesLabel = disease.category === 'dog' ? 'Köpek' : 'Kedi'

  // Aynı türdeki diğer hastalıklardan bir seçki — her sayfada farklı olması için
  // listedeki konuma göre kaydırılarak alınır.
  const pool = disease.category === 'dog' ? dogDiseases : catDiseases
  const idx = pool.findIndex(d => d.slug === disease.slug)
  const related = Array.from({ length: 6 }, (_, i) => pool[(idx + i + 1) % pool.length])
    .filter(d => d && d.slug !== disease.slug)
  const metaDescription = clampDescription(
    disease.content,
    `${disease.title} hakkında belirtiler, tedavi ve korunma bilgileri.`
  )

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        keywords={`şanlıurfa veteriner, ${disease.category === 'dog' ? 'köpek hastalıkları' : 'kedi hastalıkları'}, acil veteriner, hayvan tedavisi`}
        canonicalUrl={diseaseUrl}
        structuredData={buildGraph(
          medicalWebPageSchema({
            name: disease.title,
            description: disease.content,
            url: diseaseUrl,
            symptoms: disease.symptoms,
            treatments: disease.treatment,
            animal: disease.category
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Hastalık Rehberi', url: '/hastaliklari' },
            { name: categoryLabel, url: `/hastaliklari/${categorySlug}` },
            { name: disease.title, url: diseaseUrl }
          ])
        )}
      />
      <PageHeader title={disease.title} />
      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-4 gap-8">
          <article className="lg:col-span-3 space-y-6">
            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="bg-white rounded-2xl border p-6 shadow">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary-100"><Stethoscope className="w-5 h-5 text-primary-600"/></div>
                <h2 className="text-xl font-bold">Genel Bakış</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">{disease.content}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {disease.symptoms && (
                <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="bg-white rounded-2xl border p-6 shadow">
                  <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-amber-600"/><h3 className="font-semibold">Belirtiler</h3></div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {disease.symptoms.map((s, i) => (<li key={i}>• {s}</li>))}
                  </ul>
                </motion.div>
              )}
              {disease.treatment && (
                <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="bg-white rounded-2xl border p-6 shadow">
                  <div className="flex items-center gap-2 mb-3"><Pill className="w-5 h-5 text-green-600"/><h3 className="font-semibold">Tedavi</h3></div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {disease.treatment.map((s, i) => (<li key={i}>• {s}</li>))}
                  </ul>
                </motion.div>
              )}
              {disease.prevention && (
                <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="bg-white rounded-2xl border p-6 shadow">
                  <div className="flex items-center gap-2 mb-3"><Shield className="w-5 h-5 text-blue-600"/><h3 className="font-semibold">Korunma</h3></div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {disease.prevention.map((s, i) => (<li key={i}>• {s}</li>))}
                  </ul>
                </motion.div>
              )}
            </div>

            {/* Ne zaman acil? Belirti listelerinden bağımsız, her hastalıkta geçerli uyarılar. */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h2 className="text-lg font-bold text-red-900">Ne zaman beklemeden gelmelisiniz?</h2>
              </div>
              <p className="text-sm text-red-800 leading-relaxed mb-3">
                Hangi hastalık olursa olsun aşağıdaki bulgular acil değerlendirme gerektirir.
                Bu durumlarda internetten araştırma yapmak yerine doğrudan arayın.
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-red-800 list-disc list-inside">
                <li>Nefes almakta zorlanma</li>
                <li>Dişeti veya dilin solması / morarması</li>
                <li>Havale, bilinç bulanıklığı</li>
                <li>İdrar yapamama</li>
                <li>Durmayan kusma veya kanlı ishal</li>
                <li>24 saatten uzun süren tam iştahsızlık</li>
              </ul>
              <a href={telHref} className="text-sm font-bold text-red-700 hover:underline mt-3 inline-block">
                {contactInfo.phone} — 7/24 acil
              </a>
            </div>

            {/* İlgili hastalıklar: aynı türden, her sayfada farklı seçki */}
            {related.length > 0 && (
              <div className="bg-white rounded-2xl border p-6 shadow">
                <h2 className="text-lg font-bold mb-3">{speciesLabel}lerde diğer hastalıklar</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {related.map(d => (
                    <Link
                      key={d.slug}
                      to={`/hastaliklari/${categorySlug}/${d.slug}`}
                      className="text-sm text-primary-700 hover:underline line-clamp-1"
                    >
                      {shortenTitle(d.title, 48)}
                    </Link>
                  ))}
                </div>
                <Link
                  to={`/hastaliklari/${categorySlug}`}
                  className="text-sm font-semibold text-primary-700 hover:underline mt-4 inline-block"
                >
                  Tüm {speciesLabel.toLowerCase()} hastalıkları →
                </Link>
              </div>
            )}

            <div className="p-5 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600 leading-relaxed">
              Bu sayfa bilgilendirme amaçlıdır ve muayene yerine geçmez. Belirtiler birden fazla
              hastalıkta ortak olabilir; kesin tanı ancak klinik muayene ve gerekli tetkiklerle konur.
            </div>
          </article>

          <aside className="lg:col-span-1 space-y-4">
            <div className="bg-gradient-to-br from-primary-50 via-white to-accent-50 border rounded-2xl p-5 shadow">
              <div className="text-xs font-semibold text-gray-600 mb-2">Kategori</div>
              <div className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700">
                {disease.category === 'dog' ? 'Köpek' : 'Kedi'} Hastalığı
              </div>
            </div>

            <div className="bg-white border rounded-2xl p-5 shadow">
              <div className="text-sm text-gray-700 mb-3">
                Hastalık belirtileri gözlemlendiğinde gecikmeden veteriner hekime başvurun. Erken teşhis, tedavi başarısını artırır.
              </div>
              <a href={telHref} className="btn-primary inline-flex items-center justify-center w-full mb-2">
                {contactInfo.phone}
              </a>
              <Link to="/contact" className="btn-secondary inline-flex items-center justify-center w-full">
                İletişim ve Randevu
              </Link>
            </div>

            <div className="bg-white border rounded-2xl p-5 shadow">
              <div className="text-sm font-semibold text-gray-900 mb-3">{speciesLabel} sağlığı</div>
              <ul className="space-y-2 text-sm">
                <li><Link to={speciesHub} className="text-primary-700 hover:underline">{speciesLabel} sağlığı ana sayfa</Link></li>
                <li><Link to={`${speciesHub}/asi-takvimi`} className="text-primary-700 hover:underline">{speciesLabel} aşı takvimi</Link></li>
                <li><Link to={`${speciesHub}/parazit-korunmasi`} className="text-primary-700 hover:underline">Parazit korunması</Link></li>
                <li><Link to={`${speciesHub}/kisirlastirma`} className="text-primary-700 hover:underline">Kısırlaştırma</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  )
}

export default DiseaseDetail


